import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import Rating from "@/components/base/Rating";
import EmptyState from "@/components/base/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { getOwnerReviews, replyToOwnerReview } from "@/services/ownerApi";
import type { OwnerReview } from "@/services/ownerApi";

type Filter = "all" | "unanswered" | "5" | "3";

export default function OwnerReviews() {
  const { token } = useAuth();
  const [reviews, setReviews] = useState<OwnerReview[]>([]);
  const [replies, setReplies] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [replyError, setReplyError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getOwnerReviews(token)
      .then((r) => active && setReviews(r))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const filtered = useMemo(() => {
    if (filter === "unanswered") return reviews.filter((r) => !r.replied && !replies[r.id]);
    if (filter === "5") return reviews.filter((r) => r.rating === 5);
    if (filter === "3") return reviews.filter((r) => r.rating <= 3);
    return reviews;
  }, [reviews, filter, replies]);

  const avg = useMemo(
    () => (reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0),
    [reviews],
  );

  const sendReply = async (id: string) => {
    const text = (drafts[id] || "").trim();
    if (!text || savingId) return;
    setSavingId(id);
    setReplyError("");
    const ok = await replyToOwnerReview(id, text, token);
    setSavingId(null);
    if (!ok) {
      setReplyError("We couldn't post your reply. Please check your connection and try again.");
      return;
    }
    setReplies((prev) => ({ ...prev, [id]: text }));
    setDrafts((prev) => ({ ...prev, [id]: "" }));
  };

  const filters: { value: Filter; label: string }[] = [
    { value: "all", label: `All (${reviews.length})` },
    { value: "unanswered", label: `Unanswered (${reviews.filter((r) => !r.replied && !replies[r.id]).length})` },
    { value: "5", label: "5 star" },
    { value: "3", label: "3 star & below" },
  ];

  return (
    <>
      <Seo title="Reviews" path="/dashboard/reviews" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">Reviews</h1>
          <p className="mt-1 text-sm text-foreground-600">Respond to customer feedback across all your listings.</p>
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-2.5">
          <span className="font-heading text-2xl font-extrabold text-foreground-950">{avg.toFixed(1)}</span>
          <div>
            <Rating value={avg} />
            <p className="mt-0.5 text-[11px] text-foreground-500">{reviews.length} total reviews</p>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1 w-fit">
        {filters.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
              filter === f.value ? "bg-primary-500 text-foreground-950" : "text-foreground-600 hover:bg-background-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-32 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
            ))}
          </div>
        ) : error ? (
          <EmptyState icon="ri-wifi-off-line" title="Couldn't load reviews" description="Please refresh and try again." />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-chat-3-line" title="Nothing to show" description="Try a different filter to see your reviews." />
        ) : (
          <div className="space-y-4">
            {filtered.map((r) => {
              const replied = r.replied || Boolean(replies[r.id]);
              return (
                <article key={r.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-sm font-bold text-foreground-950">{r.authorName}</span>
                        <Rating value={r.rating} />
                      </div>
                      <p className="mt-0.5 text-xs text-foreground-500">
                        {r.businessName} · {r.date}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                        replied ? "bg-accent-100 text-accent-900" : "bg-primary-100 text-primary-900"
                      }`}
                    >
                      <i className={replied ? "ri-checkbox-circle-fill" : "ri-time-line"}></i>
                      {replied ? "Replied" : "Awaiting reply"}
                    </span>
                  </div>

                  <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{r.comment}</p>

                  {replied && (
                    <div className="mt-3 rounded-md border-l-2 border-primary-400 bg-primary-50 px-4 py-3">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-primary-800">Your reply</p>
                      <p className="mt-1 text-sm text-foreground-700">
                        {replies[r.id] || "Thanks for your feedback — we're glad you enjoyed your visit!"}
                      </p>
                    </div>
                  )}

                  {!replied && (
                    <div className="mt-4">
                      <label htmlFor={`reply-${r.id}`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
                        Write a reply
                      </label>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                          id={`reply-${r.id}`}
                          value={drafts[r.id] || ""}
                          onChange={(e) => setDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))}
                          placeholder="Thank the customer and address their comment…"
                          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => sendReply(r.id)}
                          disabled={!(drafts[r.id] || "").trim() || savingId === r.id}
                          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
                        >
                          {savingId === r.id ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i> Sending…
                            </>
                          ) : (
                            <>
                              <i className="ri-send-plane-line"></i> Reply
                            </>
                          )}
                        </button>
                      </div>
                      {replyError && (
                        <p className="mt-2 text-xs font-medium text-secondary-700">{replyError}</p>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}